/**
 * Video access badge mapping (Free / Subscriber Only / PPV).
 *
 * Presentation only: badges never decide whether a viewer may play a title.
 * Entitlement stays with the API (should_display_ppv / is_user_need_subscription).
 *
 * Config comes from get_settings_json -> video_access_badges ({ version, badges }).
 * The API already validates and contrast-checks it; the same checks run here so a
 * stale or partial payload still renders a readable badge.
 *
 * Keep this module free of imports (node contract test evaluates it directly).
 */

export const ACCESS_TYPES = ["free", "subscriber", "ppv"];

export const DEFAULT_BADGES = {
  free: { label: "Free", background: "#1B5E20", text: "#FFFFFF", icon: "none", visible: false },
  subscriber: { label: "Subscribers", background: "#0D47A1", text: "#FFFFFF", icon: "crown", visible: true },
  ppv: { label: "PPV", background: "#B71C1C", text: "#FFFFFF", icon: "ticket", visible: true },
};

export const ICON_CLASSES = {
  play: "fas fa-play",
  star: "fas fa-star",
  lock: "fas fa-lock",
  ticket: "fas fa-ticket-alt",
  crown: "fas fa-crown",
};

export const MIN_CONTRAST = 4.5;
export const LABEL_MAX_LENGTH = 24;

function normalizeColor(value) {
  if (typeof value !== "string") return null;
  const v = value.trim();
  return /^#?[0-9a-fA-F]{6}$/.test(v) ? "#" + v.replace("#", "").toUpperCase() : null;
}

function luminance(hex) {
  const channels = [1, 3, 5].map(function (i) {
    const c = parseInt(hex.substr(i, 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

export function contrastRatio(a, b) {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

export function videoAccessType(item) {
  const type = item && typeof item.access_type === "string" ? item.access_type.toLowerCase() : "";
  return ACCESS_TYPES.indexOf(type) !== -1 ? type : null;
}

/**
 * @param {string|null} accessType  free | subscriber | ppv
 * @param {object|null} config      configData.video_access_badges
 * @returns {{type,label,background,text,iconClass}|null}  null when hidden/unknown
 */
export function resolveVideoAccessBadge(accessType, config) {
  if (ACCESS_TYPES.indexOf(accessType) === -1) return null;

  const defaults = DEFAULT_BADGES[accessType];
  const configured =
    config && config.badges && typeof config.badges === "object" && config.badges[accessType]
      ? config.badges[accessType]
      : {};

  const visible = typeof configured.visible === "boolean" ? configured.visible : defaults.visible;
  if (!visible) return null;

  let label = typeof configured.label === "string" ? configured.label.trim() : "";
  if (!label || label.length > LABEL_MAX_LENGTH) label = defaults.label;

  let background = normalizeColor(configured.background);
  let text = normalizeColor(configured.text);
  if (!background || !text || contrastRatio(background, text) < MIN_CONTRAST) {
    background = defaults.background;
    text = defaults.text;
  }

  let icon = defaults.icon;
  if (
    configured.icon === "none" ||
    (typeof configured.icon === "string" && Object.prototype.hasOwnProperty.call(ICON_CLASSES, configured.icon))
  ) {
    icon = configured.icon;
  }

  return {
    type: accessType,
    label: label,
    background: background,
    text: text,
    iconClass: ICON_CLASSES[icon] || null,
  };
}
